import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { DrupalData } from './drupal-data.model';
import * as DrupalDataActions from './drupal-data.actions';
import { JsonApiQuery } from '../json-api.query';
import { isEqual } from 'lodash-es';

export const drupalDatasFeatureKey = 'drupalDatas';

export interface State extends EntityState<DrupalData> {
  // additional entities state properties
  menus: {
    [lang: string]: any;
  };
  requests: {
    [lang: string]: {
      [queryId: string]: JsonApiQuery;
    };
  };
  lang?: string;
}

export function selectDataId(a: DrupalData): string {
  //In this case this would be optional since primary key is id
  return `${a.language}*${a.type}*${a.id}`;
}

export const adapter: EntityAdapter<DrupalData> =
  createEntityAdapter<DrupalData>({
    selectId: selectDataId,
  });

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  menus: {},
  requests: {},
});

export const reducer = createReducer(
  initialState,
  on(DrupalDataActions.setState, (state, { newState }) => {
    return newState;
  }),
  on(DrupalDataActions.addDrupalData, (state, action) =>
    adapter.addOne(action.drupalData, state)
  ),
  on(DrupalDataActions.upsertDrupalData, (state, action) =>
    adapter.upsertOne(action.drupalData, state)
  ),
  on(DrupalDataActions.addDrupalDatas, (state, action) =>
    adapter.addMany(action.drupalDatas, state)
  ),
  on(DrupalDataActions.upsertDrupalDatas, (state, action) =>
    adapter.upsertMany(action.drupalDatas, state)
  ),
  on(DrupalDataActions.updateDrupalData, (state, action) =>
    adapter.updateOne(action.drupalData, state)
  ),
  on(DrupalDataActions.updateDrupalDatas, (state, action) =>
    adapter.updateMany(action.drupalDatas, state)
  ),
  on(DrupalDataActions.deleteDrupalData, (state, action) =>
    adapter.removeOne(action.id, state)
  ),
  on(DrupalDataActions.deleteDrupalDatas, (state, action) =>
    adapter.removeMany(action.ids, state)
  ),
  on(DrupalDataActions.loadDrupalDatas, (state, action) =>
    adapter.setAll(action.drupalDatas, state)
  ),
  on(DrupalDataActions.clearDrupalDatas, (state) => adapter.removeAll(state)),
  /*on(DrupalDataActions.setMainMenu, (state, { menu }) => {
      return {
        ...state,
        mainMenu: menu,
      };
    }),*/
  on(DrupalDataActions.setTypologiesMenu, (state, { menu }) => {
    const lang = state.lang;
    if (lang) {
      return {
        ...state,
        menus: {
          ...state.menus,
          [lang]: {
            ...state.menus[lang],
            typologies: menu,
          },
        },
      };
    }
    return state;
  }),
  on(DrupalDataActions.setMainMenu, (state, { menu }) => {
    const lang = state.lang;
    if (lang) {
      return {
        ...state,
        menus: {
          ...state.menus,
          [lang]: {
            ...state.menus[lang],
            main: menu,
          },
        },
      };
    }
    return state;
  }),
  on(DrupalDataActions.setMenu, (state, { menu, key }) => {
    const lang = state.lang;
    if (lang) {
      return {
        ...state,
        menus: {
          ...state.menus,
          [lang]: {
            ...state.menus[lang],
            [key]: menu,
          },
        },
      };
    }
    return state;
  }),
  on(DrupalDataActions.setLanguage, (state, { lang }) => {
    return {
      ...state,
      lang,
    };
  }),
  on(DrupalDataActions.sendRequest, (state, { request, contextId }) => {
    const lang = state.lang;
    if (lang && request) {
      let existingRequest: JsonApiQuery =
        state.requests?.[lang]?.[request.queryId];

      if (!existingRequest) {
        existingRequest = { ...request };
      } else {
        existingRequest = { ...existingRequest, ...request };
        existingRequest.resultIds = [];
        if (
          (request.editable && request.page?.offset === 0) ||
          !request.page?.offset
        ) {
          existingRequest.complete = false;
          existingRequest.allResultIds = [];
        }
      }
      let requestInternalId: string = existingRequest.queryId;
      if (request.id) {
        requestInternalId = `${existingRequest.queryId}-${request.id}`;
      }
      if (contextId) {
        requestInternalId = `${existingRequest.queryId}-${contextId}`;
      }
      existingRequest.error = undefined;
      existingRequest.loading = true;
      return {
        ...state,
        requests: {
          ...state.requests,
          [lang]: {
            ...state.requests[lang],
            [requestInternalId]: existingRequest,
          },
        },
      };
    }
    return state;
  }),
  on(
    DrupalDataActions.sendRequestSuccess,
    (state, { request, ids, contextId, canNext }) => {
      const lang = state.lang;
      if (lang && request) {
        let existingRequest: JsonApiQuery =
          state.requests?.[lang]?.[request.queryId];

        if (existingRequest?.page && request?.page) {
          if (existingRequest.page.offset !== request.page.offset) {
            return state;
          }
        }

        if (existingRequest?.filters && request?.filters) {
          if (!isEqual(existingRequest?.filters, request?.filters)) {
            return state;
          }
        }

        if (!existingRequest) {
          existingRequest = { ...request };
        } else {
          existingRequest = { ...existingRequest };
        }
        if (
          existingRequest.allResultIds &&
          ids &&
          request.page &&
          request.page.offset > 0
        ) {
          existingRequest.allResultIds = [
            ...existingRequest.allResultIds,
            ...ids,
          ];
        } else if (ids) {
          existingRequest.allResultIds = [...ids];
        }
        let requestInternalId: string = existingRequest.queryId;
        if (request.id) {
          requestInternalId = `${existingRequest.queryId}-${request.id}`;
        }
        if (contextId) {
          requestInternalId = `${existingRequest.queryId}-${contextId}`;
        }
        existingRequest.complete = !canNext;
        existingRequest.loading = false;
        return {
          ...state,
          requests: {
            ...state.requests,
            [lang]: {
              ...state.requests[lang],
              [requestInternalId]: existingRequest,
            },
          },
        };
      }
      return state;
    }
  ),
  on(
    DrupalDataActions.sendRequestError,
    (state, { error, request, contextId }) => {
      const lang = state.lang;
      if (lang && request) {
        let existingRequest: JsonApiQuery =
          state.requests?.[lang]?.[request.queryId];

        if (!existingRequest) {
          existingRequest = { ...request };
        } else {
          existingRequest = { ...existingRequest };
        }
        existingRequest.loading = false;
        existingRequest.complete = false;
        existingRequest.error = error;
        let requestInternalId: string = existingRequest.queryId;
        if (request.id) {
          requestInternalId = `${existingRequest.queryId}-${request.id}`;
        }
        if (contextId) {
          requestInternalId = `${existingRequest.queryId}-${contextId}`;
        }
        return {
          ...state,
          requests: {
            ...state.requests,
            [lang]: {
              ...state.requests[lang],
              [requestInternalId]: existingRequest,
            },
          },
        };
      }
      return state;
    }
  )
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const selectAllDrupalData = selectAll;

export const selectDrupalDataEntities = selectEntities;
